import { useRef } from "react";
import { toast } from "sonner";

import type { PriceCategoryType } from "Infrastructure/Api/Api";
import { Resources, useResource } from "Infrastructure/Translations/Resources";
import { useCreatePriceCategoryMutation } from "PriceCategories/Api/Mutations/useCreatePriceCategoryMutation";
import {
  PriceCategoryForm,
  type PriceCategoryFormHandle,
} from "PriceCategories/Forms/PriceCategoryForm";
import type { PriceCategoryFormData } from "PriceCategories/Forms/priceCategoryFormSchema";
import { BlModal } from "Shared/Dialogs/BlModal";

interface CreatePriceCategoryModalProps {
  isOpen: boolean;
  onClose: () => void;
}

export const CreatePriceCategoryModal = ({
  isOpen,
  onClose,
}: CreatePriceCategoryModalProps): React.ReactNode => {
  const { t } = useResource();
  const formRef = useRef<PriceCategoryFormHandle | null>(null);
  const createMutation = useCreatePriceCategoryMutation();

  const onSubmit = async (data: PriceCategoryFormData) => {
    try {
      await createMutation.mutateAsync({
        name: data.name,
        type: data.type as PriceCategoryType,
        commissionRate: data.commissionRate / 100,
        defaultPrice: data.defaultPrice,
      });
      toast.success(t(Resources.PriceCategories.CreateModal.SuccessMessage));
      formRef.current?.reset();
      onClose();
    } catch {
      // error handled by hook
    }
  };

  const handleClose = () => {
    formRef.current?.reset();
    onClose();
  };

  return (
    <BlModal
      isOpen={isOpen}
      title={t(Resources.PriceCategories.CreateModal.Title)}
      onCancel={handleClose}
      onConfirm={() => formRef.current?.submit(onSubmit)()}
      cancelButtonText={t(Resources.Common.Cancel)}
      confirmButtonText={t(Resources.PriceCategories.CreateModal.Create)}
    >
      <PriceCategoryForm
        priceCategory={null}
        formRef={formRef}
        serverError={createMutation.error}
      />
    </BlModal>
  );
};
